export const defaultLocale = 'en-IN' as const;

export const supportedLocales = {
  'en-IN': {
    code: 'en-IN',
    label: 'English (India)',
    nativeLabel: 'English',
    language: 'en',
    region: 'IN',
    dir: 'ltr',
    routePrefix: '',
    hrefLang: 'en-IN',
    ogLocale: 'en_IN',
  },
  ta: {
    code: 'ta',
    label: 'Tamil',
    nativeLabel: 'தமிழ்',
    language: 'ta',
    region: 'IN',
    dir: 'ltr',
    routePrefix: '/ta',
    hrefLang: 'ta-IN',
    ogLocale: 'ta_IN',
  },
} as const;

export type LocaleCode = keyof typeof supportedLocales;
export type LocaleMetadata = (typeof supportedLocales)[LocaleCode];

export const supportedLocaleCodes = Object.keys(supportedLocales) as LocaleCode[];

export function resolveLocale(locale?: string | null): LocaleCode {
  if (locale && locale in supportedLocales) {
    return locale as LocaleCode;
  }

  return defaultLocale;
}

export function getLocaleMetadata(locale?: string | null): LocaleMetadata {
  return supportedLocales[resolveLocale(locale)];
}

export function isLocaleCode(value?: string | null): value is LocaleCode {
  return Boolean(value && value in supportedLocales);
}

export function splitPathSuffix(pathOrHref: string) {
  const hashIndex = pathOrHref.indexOf('#');
  const queryIndex = pathOrHref.indexOf('?');
  const suffixStart = [hashIndex, queryIndex].filter((index) => index >= 0).sort((a, b) => a - b)[0];

  if (suffixStart === undefined) {
    return { pathname: pathOrHref, suffix: '' };
  }

  return {
    pathname: pathOrHref.slice(0, suffixStart),
    suffix: pathOrHref.slice(suffixStart),
  };
}

function normalizePathname(pathname: string) {
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`;

  if (path === '/') {
    return path;
  }

  return path.replace(/\/+$/, '');
}

export function getLocaleFromPathname(pathname: string): LocaleCode {
  const normalizedPath = normalizePathname(splitPathSuffix(pathname).pathname);
  const firstSegment = normalizedPath.split('/').filter(Boolean)[0];

  if (isLocaleCode(firstSegment)) {
    return firstSegment;
  }

  return defaultLocale;
}

export function stripLocaleFromPathname(pathname: string) {
  const { pathname: rawPathname, suffix } = splitPathSuffix(pathname);
  const normalizedPath = normalizePathname(rawPathname);
  const segments = normalizedPath.split('/').filter(Boolean);

  if (isLocaleCode(segments[0])) {
    const pathWithoutLocale = `/${segments.slice(1).join('/')}`;
    return `${pathWithoutLocale === '/' ? '/' : pathWithoutLocale}${suffix}`;
  }

  return `${normalizedPath}${suffix}`;
}

export function localizePath(pathname: string, locale?: string | null) {
  const resolvedLocale = resolveLocale(locale);
  const { pathname: pathWithoutSuffix, suffix } = splitPathSuffix(stripLocaleFromPathname(pathname));
  const normalizedPath = normalizePathname(pathWithoutSuffix);
  const prefix = supportedLocales[resolvedLocale].routePrefix;

  if (!prefix) {
    return `${normalizedPath}${suffix}`;
  }

  return normalizedPath === '/' ? `${prefix}/${suffix}` : `${prefix}${normalizedPath}${suffix}`;
}

export function localizeHref(href: string, locale?: string | null) {
  if (
    href.startsWith('#') ||
    href.startsWith('mailto:') ||
    href.startsWith('tel:') ||
    href.startsWith('http://') ||
    href.startsWith('https://')
  ) {
    return href;
  }

  return localizePath(href, locale);
}

export function getAlternateLocalePaths(pathname: string) {
  const basePath = stripLocaleFromPathname(pathname);

  return supportedLocaleCodes.map((locale) => ({
    locale,
    hrefLang: supportedLocales[locale].hrefLang,
    path: localizePath(basePath, locale),
  }));
}
